<script setup>
import IEventItem from "@components/IEventItem/IEventItem.vue";
import ITeams from '@components/ITeams/ITeams.vue';
import { translator } from "@shop/frontend/web/js/services/TranslatorService";
import { dateHelper } from "@shop/frontend/web/js/helpers/dateHelper";
import EventsItemsEmpty from "./EventsItemsEmpty.vue";
import EventItemsSkeleton from "./EventItemsSkeleton.vue";
import { DEFAULT_ICON_URL } from "@shop/frontend/web/js/constants/event";
import useSiteStore from "../../store/modules/site";
import { computed, watch } from "vue";

const store = useSiteStore();

store.searchEvents();
store.searchSubscriptions();
store.searchServices();
store.searchPerformances();

const loadingData = computed(
	() =>
		store.getSubscriptionsState.loading ||
		store.getEventsState.loading ||
		store.getServicesState.loading ||
		store.getPerformancesState.loading
);

const showEvents = computed(
	() =>
		!!store.getEventsState.items.length &&
		(!store.getSelectTypes.items.length || store.getSelectTypes.items.includes(1))
);
const showSubscriptions = computed(
	() =>
		!!store.getSubscriptionsState.items.length &&
		(!store.getSelectTypes.items.length || store.getSelectTypes.items.includes(2))
);
const showServices = computed(
	() =>
		!!store.getServicesState.items.length &&
		(!store.getSelectTypes.items.length || store.getSelectTypes.items.includes(3))
);
const showPerformances = computed(
	() =>
		!!store.getPerformancesState.items.length &&
		(!store.getSelectTypes.items.length || store.getSelectTypes.items.includes(4))
);

watch(loadingData, () => (loadingData.value ? $("#js-overlay").show() : $("#js-overlay").hide()));
</script>

<template>
	<event-items-skeleton :is-data-loading="loadingData">
		<events-items-empty>
			<div v-if="showEvents" class="mb-5">
				<span class="event-item__title">
          {{ translator.t("main", "Matches") }}
          <span class="event-item__title_small">
            {{ translator.t("main", "Matches") }}
          </span>
        </span>
				<i-event-item
					v-for="event in store.getEventsState.items"
					:key="event.id"
					:event="event"
					:isPackage="event.eventType === 'Пакет'"
					class="mb-3"
				>
					<template #name v-if="event.eventType !== 'Пакет'">
						<i-teams
							:is-home-logo-with-opponent="event.settings.isHomeLogoWithOpponent"
              :is-home-logo-without-opponent="event.settings.isHomeLogoWithoutOpponent"
              :is-show-tournament="event.settings.isShowTournament"
              :name="event.name"
							:first-team="
								event.participants.team?.homeTeam || event.participants.singular
							"
							:second-team="event.participants.team?.guestTeam"
							:to="!event.settings.isSoon ? event.pageLink : ''"
							class="teams_main"
						></i-teams>
					</template>
				</i-event-item>
				<div v-if="store.getEventsState.showLoadMore" class="text-center">
					<button class="i-btn i-btn_outlined" @click="store.loadMoreEvents">
						{{ translator.t("main", "More") }}
					</button>
				</div>
			</div>

			<div v-if="showSubscriptions" class="mb-5">
        <span class="event-item__title">
          {{ translator.t("main", "Season tickets") }}
          <span class="event-item__title_small">
            {{ translator.t("main", "Season tickets") }}
          </span>
        </span>
				<i-event-item
					v-for="event in store.getSubscriptionsState.items"
					:key="event.id"
					:event="event"
					class="mb-3"
				></i-event-item>
				<div v-if="store.getSubscriptionsState.showLoadMore" class="text-center">
					<button class="i-btn i-btn_outlined" @click="store.loadMoreSubscriptions">
						{{ translator.t("main", "More") }}
					</button>
				</div>
			</div>

			<div v-if="showServices" class="mb-5">
				<div class="fs-3 fw-bold lh-sm mb-4">{{ translator.t("event", "Services") }}</div>
				<div :class="{ row: store.getServicesState.items.length > 0 }">
					<div
						v-for="event in store.getServicesState.items"
						:key="event.id"
						class="col-md-6"
					>
						<i-event-item :event="event" is-short class="mb-3">
							<template #name>
								<div class="d-flex w-100">
									<div class="i-event-item__image mr-3">
										<img
											:src="event.imagePath || DEFAULT_ICON_URL"
											:alt="event.name"
											width="82"
											height="110"
										/>
									</div>
									<div>
										<div class="i-event-item__info-wrapper">
											<span class="i-event-item__age-limit">
												{{ event.ageLimit }}+
											</span>
											<div v-if="event.hasPushkinPay">
												<svg width="24" height="24" class="icon">
													<use
														xlink:href="#pushkin-in-square-border-radius"
													></use>
												</svg>
											</div>
										</div>
										<div class="i-event-item__name">{{ event.name }}</div>
										<div class="i-event-item__sub-name mt-1"></div>
										<span class="i-event-item__min-price">
											{{ translator.t("main", "from ") }}
											{{ event.minPrice }} ₽
										</span>
									</div>
								</div>
							</template>
						</i-event-item>
					</div>
				</div>
				<div v-if="store.getServicesState.showLoadMore" class="text-center">
					<button class="i-btn i-btn_outlined" @click="store.loadMoreServices">
						{{ translator.t("main", "More") }}
					</button>
				</div>
			</div>

			<div v-if="showPerformances" class="mb-5">
				<div class="fs-3 fw-bold lh-sm mb-4">{{ translator.t("event", "Events") }}</div>
				<i-event-item
					v-for="event in store.getPerformancesState.items"
					:key="event.id"
					:event="event"
					:is-perfomance="true"
					class="mb-3"
				>
					<template #info>
						<div class="i-event-item__perfomance-info-wrapper">
							<a :href="event.pageLink">
								<div class="i-event-item__image">
									<img
										:src="event.participants.singular.imageUrl || DEFAULT_ICON_URL"
										:alt="event.participants.singular.name"
										width="82"
										height="110"
									/>
								</div>
							</a>
							<div class="d-flex d-sm-none i-event-item__perfomance-info-right">
								<span class="i-label i-label_small i-label_primary i-label_perfomance mr-2">
									{{ event.ageLimit }}+
								</span>
								<a :href="event.pageLink" class="i-event-item__name">
									{{ event.participants.singular.name }}
								</a>
							</div>
						</div>
					</template>
					<template #name>
						<div class="d-flex flex-column align-items-sm-center">
							<div class="i-event-item-info__date-wrapper">
								<div
									v-if="event.settings.isShowDate"
									class="d-inline-flex align-items-center text-primary i-event-item-info__text text-nowrap mb-2"
								>
									<svg
										class="icon i-icon_stroke-color i-icon_sm mr-2"
										width="15"
										height="15"
									>
										<use xlink:href="#calendar"></use>
									</svg>
									<span class="text-capitalize">
										{{ dateHelper.getFormattedDate(event.startTimestamp, 'DD.MM.YYYY dd HH:mm') }}
									</span>
								</div>
								<div
									v-if="event.settings.isShowUnit"
									class="d-inline-flex align-items-center i-event-item-info__text text-nowrap mb-sm-2"
								>
									<svg
										class="icon i-icon_stroke-color i-icon_sm mr-2"
										width="15"
										height="15"
									>
										<use xlink:href="#location"></use>
									</svg>
									<span>
										{{ event.unitName }}
									</span>
								</div>
							</div>
							<div class="d-none d-sm-flex align-items-center">
								<span class="i-label i-label_small i-label_primary mr-2"
									>{{ event.ageLimit }}+</span
								>
								<a :href="event.pageLink" class="i-event-item__name">
									{{ event.participants.singular.name }}
								</a>
							</div>
						</div>
					</template>
				</i-event-item>
				<div v-if="store.getPerformancesState.showLoadMore" class="text-center">
					<button class="i-btn i-btn_outlined" @click="store.loadMorePerformances">
						{{ translator.t("main", "More") }}
					</button>
				</div>
			</div>
		</events-items-empty>
	</event-items-skeleton>
</template>

<style lang="scss">
.event-item {
  &__title {
    position: relative;
    font-weight: 900;
    font-size: 80px;
    color: rgba(255, 117, 25, 0.09);
  }

  &__title_small {
    content: '';
    position: absolute;
    bottom: 5px;
    left: 3px;
    font-weight: 700;
    font-size: 36px;
    color: rgba(0, 11, 77, 1);
  }

  @media (max-width: $screen-xs) {
    &__title {
      font-weight: 800;
      font-size: 45px;
    }

    &__title_small {
      content: '';
      bottom: 3px;
      left: 2px;
      font-weight: 600;
      font-size: 20px;
    }
  }
}
</style>
